"use client"

import { useEffect, useMemo, useState } from "react"
import {
  AddCandidateDialog,
  type AddCandidateSource,
} from "@/components/add-candidate-dialog"
import { EmptyStage, openedFact, stageRoutes } from "@/components/jobs/empty-stage"
import { PipelineCandidateCard } from "@/components/pipeline-candidate-card"
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd"
import { PIPELINE_STAGES } from "@/lib/mock-data"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
//import { PlacementDialog } from "@/components/placement-dialog"
import {
  useGetJobsJobId,
  useGetJobsJobIdPipeline,
  usePutJobsJobIdPipelineStage,
} from "openapi/api/endpoints"
import type { GetJobsJobIdPipeline200 } from "openapi/api/model"
//import { queryClient } from "@/App"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { toast } from "sonner"
import { PlacementDialog } from "./placement-dialog"
import { PipelineStageEventDialog } from "./pipeline-stage-event-dialog"
import { stageHex } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"

type HiredPlacementContext = {
  candidateId: string
}

type PipelineColumn = NonNullable<GetJobsJobIdPipeline200[string]>


/** Moving a candidate without a mouse. */
function MoveStageMenu({
  stages,
  current,
  onMove,
}: {
  stages: string[]
  current: string
  onMove: (next: string) => void
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="shrink-0 whitespace-nowrap px-1 text-[11.5px] text-brand-ink transition-opacity hover:opacity-80"
        >
          Move
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {stages
          .filter((stage) => stage !== current)
          .map((stage) => (
            <DropdownMenuItem key={stage} onSelect={() => onMove(stage)}>
              <span
                className="mr-2 h-[5px] w-[5px] rounded-full"
                style={{ backgroundColor: stageHex(stage) }}
              />
              {stage}
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

type JobPipelineTabProps = {
  jobId: string
  /**
   * Opens a candidate's profile. The overlay lives on the job page, not here,
   * so Today can open the same profile; this tab only asks for it.
   */
  onOpenCandidate: (candidateId: string) => void
}

export function JobPipelineTab({ jobId, onOpenCandidate }: JobPipelineTabProps) {
  const queryClient = useQueryClient()
  const { data:job } = useGetJobsJobId(jobId, {query:{enabled:!!jobId}})
  const navigate = useNavigate()
  const { data, refetch } = useGetJobsJobIdPipeline(jobId, {
    query: { enabled: !!jobId },
  })
  const { mutateAsync: moveCandidateStage } = usePutJobsJobIdPipelineStage()
  const [showStageDialog, setShowStageDialog] = useState(false)
  const [stageType, setStageType] = useState<"interviewing" | "screening">("interviewing")
  const [candidateEventId,setCandidateEventId]=useState<string | undefined>(undefined)
  const [hiredPlacementContext, setHiredPlacementContext] = useState<HiredPlacementContext | null>(null)
  /*
    18c's empty state is three ways out of an empty stage. Two of them open the
    add surfaces this tab already renders, so the request lives here rather than
    each row owning its own copy of them.
  */
  const [addRequest, setAddRequest] = useState<AddCandidateSource | null>(null)
  /** Narrow layouts show one stage at a time; this is which. */
  const [openStage, setOpenStage] = useState<string | null>(null)

  const pipeline = useMemo<GetJobsJobIdPipeline200>(() => data ?? {}, [data])

  // Local UI state for columns
  const [columns, setColumns] = useState(pipeline)

  // Keep local state synced with server data when it changes
  useEffect(() => {
    if (pipeline && Object.keys(pipeline).length > 0) {
      setColumns(pipeline)
    }
  }, [pipeline])

  if(!data){
    return(
      <div>No pipelines for this job!</div>
    )
  }



  const getColumnColor = (columnId: string) => {
    return PIPELINE_STAGES[columnId as keyof typeof PIPELINE_STAGES]?.color || "border-t-gray-300"
  }

  const handleOnDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result

    if (!destination) return
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return

    // Both layouts are mounted at once (one is hidden), so the narrow list
    // namespaces its ids to avoid colliding with the board's. Strip it before
    // the ids are used as stage names and candidate ids.
    const unprefix = (value: string) => value.replace(/^mobile-/, "")

    await moveCandidateTo(
      unprefix(draggableId),
      unprefix(source.droppableId),
      unprefix(destination.droppableId),
      destination.index,
    )
  }

  /**
   * The one path a candidate changes stage by — used by drag-and-drop on both
   * layouts and by the narrow layout's Move menu, so there is a single call into
   * the stage API with a single optimistic update and rollback.
   */
  const moveCandidateTo = async (
    candidateId: string,
    oldStage: string,
    newStage: string,
    atIndex?: number,
  ) => {
    if (oldStage === newStage) return

    // Save previous state for rollback
    const prevColumns = structuredClone(columns)

    // Optimistically update local UI
    const newColumns = structuredClone(columns)
    const sourceColumn = newColumns[oldStage]
    const destinationColumn = newColumns[newStage]
    if (!sourceColumn || !destinationColumn) return

    sourceColumn.candidateIds = sourceColumn.candidateIds?.filter(
      (id) => id !== candidateId
    )
    destinationColumn.candidateIds ??= []
    destinationColumn.candidateIds.splice(
      atIndex ?? destinationColumn.candidateIds.length,
      0,
      candidateId,
    )
    setColumns(newColumns)
    try {
      // Server update
      await moveCandidateStage({
        jobId,
        data: {
          candidate_id: candidateId,
          stage: newStage,
        },
      })

      // Refresh server data
      await queryClient.invalidateQueries({ queryKey: [`/jobs/${jobId}/pipeline`],exact:false })
      await refetch()
    } catch (error) {
      console.error("Error moving candidate:", error)
      setColumns(prevColumns) // rollback UI
      toast.error("An error occured!",{
        description: `${error}`,
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: "#dc2626",
        },
      });
      return
    }

    if(newStage==='hired'){
      setHiredPlacementContext({ candidateId })
    }

    if(newStage==='interviewing' || newStage==='screening'){
      setCandidateEventId(candidateId)
      setStageType(newStage)
      setShowStageDialog(true)
    }
  }

  const STAGE_ORDER = [
    "longlist",
    "shortlist",
    "outbound",
    "screening",
    "submitted",
    "interviewing",
    "hired",
    "rejected",
  ]
  

  /*
    Canvas 4b: a conventional 288px kanban with horizontal scroll. No heading —
    the tab already says Pipeline, and the canvas gives the board the full
    height. Columns are hairline-topped in their stage colour rather than being
    cards, so the board reads as one surface with eight lanes.
  */
  const orderedStages = Object.entries(columns)
    .map(([stageId, column]) => ({ stageId, column: column as PipelineColumn }))
    .sort((a, b) => STAGE_ORDER.indexOf(a.stageId) - STAGE_ORDER.indexOf(b.stageId))

  const activeStage =
    orderedStages.find((entry) => entry.stageId === openStage) ?? orderedStages[0]

  const isStageEmpty = (activeStage?.column.candidateIds ?? []).length === 0

  const routes = stageRoutes({
    onSearchPool: () => setAddRequest("pool"),
    onAddByName: () => setAddRequest("new"),
    onNewSearch: () => navigate({ to: "/searches" }),
  })

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      {/*
        18c draws no action row over an empty stage: the three routes below are
        the affordance, and repeating two of them here is the duplication the
        section is removing. The row comes back as soon as there is a list.
      */}
      <div
        className={cn(
          "shrink-0 items-center justify-end pb-3",
          isStageEmpty ? "hidden @3xl:flex" : "flex",
        )}
      >
        <AddCandidateDialog
          requested={addRequest}
          onRequestHandled={() => setAddRequest(null)}
        />
      </div>

      <DragDropContext onDragEnd={handleOnDragEnd}>
      {/*
        Narrow (canvas 8e): one open stage at a time behind a scrollable chip
        strip that carries every count, so the shape of the pipeline stays legible
        without eight lanes.

        Both layouts sit inside the single DragDropContext below and use the same
        Droppable/Draggable pair, so there is one drag implementation and one path
        to the stage API — @hello-pangea/dnd handles touch, so dragging works here
        too. `Move` is the explicit affordance the canvas shows, and it calls the
        same `moveCandidateTo`; it is a second control, not a second mechanism.
      */}
      <div className="flex min-h-0 flex-1 flex-col @3xl:hidden">
        {/*
          18c: "Stages were rounded pills in a scrolling row, then the active
          stage was printed again underneath as a heading. Now they are one text
          rail with a colour dot and a count, active underlined in teal — the
          same rail as the desktop board."
        */}
        <div className="relative shrink-0">
          <div className="flex gap-[18px] overflow-x-auto border-b pr-7">
            {orderedStages.map(({ stageId, column }) => {
              const selected = stageId === activeStage?.stageId
              return (
                <button
                  key={stageId}
                  type="button"
                  onClick={() => setOpenStage(stageId)}
                  aria-current={selected ? "true" : undefined}
                  className={cn(
                    "inline-flex shrink-0 items-center gap-[7px] whitespace-nowrap border-b-2 pb-2.5 text-[12.5px] transition-colors",
                    selected
                      ? "border-primary font-medium text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  <span
                    className="h-[6px] w-[6px] rounded-full"
                    style={{ backgroundColor: stageHex(stageId) }}
                  />
                  {column.title ?? stageId}
                  <span className="font-mono text-[10.5px] text-muted-foreground">
                    {column.candidateIds?.length ?? 0}
                  </span>
                </button>
              )
            })}
          </div>
          <span className="pointer-events-none absolute inset-y-0 right-0 w-7 bg-gradient-to-r from-transparent to-background" />
        </div>

        {activeStage && (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <Droppable droppableId={`mobile-${activeStage.stageId}`}>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {(activeStage.column.candidateIds ?? []).map((candidateId, index) => (
                    <Draggable
                      key={candidateId}
                      draggableId={`mobile-${candidateId}`}
                      index={index}
                    >
                      {(dragProvided, snapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className={`flex items-center gap-2.5 border-t py-3 ${snapshot.isDragging ? "shadow-lg" : ""}`}
                        >
                          <div className="min-w-0 flex-1">
                            <PipelineCandidateCard
                              jobId={jobId}
                              candidateId={candidateId}
                              onClick={() => onOpenCandidate(candidateId)}
                            />
                          </div>
                          <MoveStageMenu
                            stages={STAGE_ORDER}
                            current={activeStage.stageId}
                            onMove={(next) =>
                              void moveCandidateTo(candidateId, activeStage.stageId, next)
                            }
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            {(activeStage.column.candidateIds ?? []).length === 0 && (
              <EmptyStage
                stage={activeStage.column.title ?? activeStage.stageId}
                openedFact={openedFact(job?.company_name, job?.posted_at)}
                routes={routes}
              />
            )}
          </div>
        )}
      </div>

        <div className="hidden min-h-0 flex-1 items-start gap-2.5 overflow-x-auto overflow-y-hidden pb-6 @3xl:flex">
          {/*
            Keyed by the stage name, not `column.id`. The payload does not always
            carry an id, and the previous `if (!column.id) return null` meant the
            entire board rendered nothing when it was absent — a silent, total
            failure. The key is authoritative; the id is a convenience.
          */}
          {Object.entries(columns)
          .map(([stageId, column]) => ({ stageId, column: column as PipelineColumn }))
          .sort(
            (a, b) => STAGE_ORDER.indexOf(a.stageId) - STAGE_ORDER.indexOf(b.stageId)
          )
          .map(({ stageId, column }) => {
            return (
            <div
              key={stageId}
              className="flex h-full w-[288px] shrink-0 flex-col gap-2"
            >
              <div
                className={`flex shrink-0 items-baseline gap-2 border-t-[3px] px-0.5 pt-2.5 ${getColumnColor(stageId)}`}
              >
                <span className="text-xs">{column.title ?? stageId}</span>
                <span className="ml-auto font-mono text-xs font-medium text-muted-foreground">
                  {column.candidateIds?.length || 0}
                </span>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                  <Droppable droppableId={stageId}>
                    {(provided, snapshot) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className={`min-h-[200px] space-y-2 rounded-lg pb-2 ${snapshot.isDraggingOver ? "bg-foreground/[0.04]" : ""}`}
                      >
                        {(column.candidateIds || []).map((candidateId: string, index: number) => (
                          <Draggable key={candidateId.toString()} draggableId={candidateId.toString()} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`${snapshot.isDragging ? "rotate-2 shadow-lg" : ""}`}
                              >
                                <PipelineCandidateCard
                                  jobId={jobId}
                                  candidateId={candidateId}
                                  onClick={() => {
                                    onOpenCandidate(candidateId)
                                  }}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
              </div>
            </div>
          )
          })}
        </div>
      </DragDropContext>
      {hiredPlacementContext &&  (
        <PlacementDialog
          isOpen={true}
          onClose={() => setHiredPlacementContext(null)}
          jobTitle={job?.title??''}
          companyName={job?.company_name??''}
          candidateId={hiredPlacementContext.candidateId}
          jobId={jobId}
          creationDate={(job as {created_at?:string})?.created_at??''}
        />
      )}
      {showStageDialog && (
        <PipelineStageEventDialog
          open={showStageDialog}
          onOpenChange={(open) => {
            setShowStageDialog(open)
            if (!open) {
              setCandidateEventId(undefined)
            }
          }}
          candidateId={candidateEventId}
          stage={stageType}
        />
      )}
    </div>
  )
}
